'use client';

import { useEffect, useRef, useState } from 'react';
import { Mail, Send } from 'lucide-react';

const total = 12;

export function ExtensionPreview() {
  const [sent, setSent] = useState(0);
  const [phase, setPhase] = useState<'running' | 'done' | 'waiting'>('waiting');

  // Start first run after mount
  useEffect(() => {
    const id = setTimeout(() => setPhase('running'), 800);
    return () => clearTimeout(id);
  }, []);

  // Tick while running
  useEffect(() => {
    if (phase !== 'running') return;
    if (sent >= total) {
      setPhase('done');
      return;
    }
    const id = setTimeout(() => setSent((s) => s + 1), 1000);
    return () => clearTimeout(id);
  }, [phase, sent]);

  // Restart loop after done
  useEffect(() => {
    if (phase !== 'done') return;
    const id = setTimeout(() => {
      setSent(0);
      setPhase('running');
    }, 4000);
    return () => clearTimeout(id);
  }, [phase]);

  const progress = (sent / total) * 100;

  return (
    <div className="w-full max-w-xs">
      <div className="glass-card rounded-xl p-5 shadow-2xl space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
            style={{
              background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
            }}
          >
            H
          </div>
          <div>
            <div className="text-sm font-bold tracking-tight">
              <span style={{ color: '#7c3aed' }}>Hy</span>
              <span className="text-base-content">Pe</span>
              <span style={{ color: '#7c3aed' }}>Ma</span>
            </div>
            <p className="text-[7px] uppercase tracking-[0.12em] text-base-content/30 font-semibold">
              Gmail Draft Creator
            </p>
          </div>
        </div>

        {/* Batch progress */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className={`w-2 h-2 rounded-full ${phase === 'done' ? 'bg-success' : 'animate-pulse'}`}
                style={phase !== 'done' ? { background: '#7c3aed' } : undefined}
              />
              <span className="text-xs font-medium text-base-content/70">
                {phase === 'done' ? 'All sent!' : 'Sending emails...'}
              </span>
            </div>
            <span
              className="text-xs font-bold font-mono-stat"
              style={{ color: '#7c3aed' }}
            >
              {sent}/{total}
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 rounded-full bg-base-300/30 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #7c3aed, #ec4899)',
              }}
            />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center bg-base-100/50 rounded-lg py-2">
              <div className="text-sm font-bold text-success">{sent}</div>
              <div className="text-[9px] uppercase text-base-content/30 font-semibold">
                Sent
              </div>
            </div>
            <div className="text-center bg-base-100/50 rounded-lg py-2">
              <div className="text-sm font-bold text-error/60">0</div>
              <div className="text-[9px] uppercase text-base-content/30 font-semibold">
                Failed
              </div>
            </div>
            <div className="text-center bg-base-100/50 rounded-lg py-2">
              <div className="text-sm font-bold text-base-content/40">
                {total - sent}
              </div>
              <div className="text-[9px] uppercase text-base-content/30 font-semibold">
                Remaining
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-2 pt-1">
          <button className="btn btn-ghost btn-sm flex-1 gap-1 border-base-300/30 text-xs">
            <Mail size={12} /> Gmail
          </button>
          <button
            className="btn btn-sm flex-1 gap-1 text-white text-xs border-0"
            style={{
              background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
            }}
          >
            <Send size={12} /> HyPeMa
          </button>
        </div>
      </div>
    </div>
  );
}
